import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import StatusBadge from "@/components/StatusBadge";
import POTimeline from "@/components/POTimeline";
import { POLineItemsDetail } from "@/components/POLineItemTable";
import { fmtDate, fmtDateTime, fmtINR } from "@/lib/format";
import { ArrowLeft, CheckCircle2, FileEdit, Send, Truck, UploadCloud, FileDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

export default function SupplierPODetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ status: "", expected_delivery_date: "", dispatch_date: "", transport_mode: "", tracking_ref: "", manufacturing_status: "", comment: "" });
  const [modOpen, setModOpen] = useState(false);
  const [mod, setMod] = useState({ requested_date: "", reason: "" });
  const [saving, setSaving] = useState(false);
  const [docs, setDocs] = useState([]);
  const [docType, setDocType] = useState("invoice");
  const [uploading, setUploading] = useState(false);

  const load = useCallback(() => {
    api.get(`/pos/${id}`).then((r) => setData(r.data)).catch(() => toast.error("PO not found"));
    api.get(`/pos/${id}/documents`).then((r) => setDocs(r.data.documents)).catch(() => {});
  }, [id]);
  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="py-20 text-center text-sm text-muted-foreground">Loading PO…</div>;
  const { po, events, communications, modifications } = data;
  const needsAck = ["po_created", "po_sent", "ack_pending"].includes(po.status);
  const pendingMod = modifications.find((m) => m.status === "pending");

  const acknowledge = async () => {
    try {
      await api.post(`/supplier/pos/${id}/update`, { acknowledge: true });
      toast.success("PO acknowledged — buyer notified in real time");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const submitUpdate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = Object.fromEntries(Object.entries(form).filter(([, v]) => v));
      await api.post(`/supplier/pos/${id}/update`, payload);
      toast.success("PO updated — buyer dashboard refreshed");
      setForm({ status: "", expected_delivery_date: "", dispatch_date: "", transport_mode: "", tracking_ref: "", manufacturing_status: "", comment: "" });
      load();
    } catch (err) { toast.error(formatApiError(err)); } finally { setSaving(false); }
  };

  const submitMod = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/pos/${id}/modification`, mod);
      toast.success("Modification request sent to buyer");
      setModOpen(false);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const uploadDoc = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("doc_type", docType);
      await api.post(`/pos/${id}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document uploaded — recorded on the PO timeline");
      load();
    } catch (err) { toast.error(formatApiError(err)); } finally { setUploading(false); }
  };

  return (
    <div data-testid="supplier-po-detail" className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <Button variant="ghost" size="icon" data-testid="back-btn" onClick={() => navigate(-1)}><ArrowLeft className="h-4 w-4" /></Button>
        <div className="flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span data-testid="po-buyer-org" className="inline-flex items-center gap-1.5 rounded-md border bg-slate-900 px-2.5 py-1 text-xs font-semibold text-white">
              Buyer Organization: {po.buyer_org_name || "—"}
            </span>
            <span className="text-xs text-muted-foreground">Buyer Contact: {po.buyer_name}</span>
          </div>
          <div className="mt-1.5 flex items-center gap-3">
            <h1 className="font-mono font-heading text-2xl font-semibold tracking-tight">{po.po_number}</h1>
            <StatusBadge status={po.status} />
          </div>
          <p className="text-sm text-muted-foreground">{po.item} · {po.quantity} {po.uom} · {fmtINR(po.value)} · Supplier: {po.supplier_name}</p>
        </div>
        {needsAck && <Button data-testid="acknowledge-btn" onClick={acknowledge} className="bg-[#166534] hover:bg-[#14532D]"><CheckCircle2 className="mr-1.5 h-4 w-4" /> Acknowledge PO</Button>}
        {!needsAck && !pendingMod && !["delivered", "closed", "cancelled"].includes(po.status) && (
          <Button variant="outline" data-testid="request-modification-btn" onClick={() => setModOpen(true)}><FileEdit className="mr-1.5 h-4 w-4" /> Request Date Change</Button>
        )}
      </div>

      <div className="rounded-lg border bg-card">
        <h2 className="border-b px-5 py-3 font-heading text-sm font-semibold">PO Line Items</h2>
        <POLineItemsDetail po={po} testId="supplier-po-line-items" />
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="space-y-4 lg:col-span-2">
          <div className="rounded-lg border bg-card">
            <h2 className="border-b px-5 py-3 font-heading text-sm font-semibold">PO & Delivery Details</h2>
            <div className="grid grid-cols-2 gap-x-8 gap-y-2.5 p-5 text-sm md:grid-cols-3">
              {[
                ["Buyer Organization", po.buyer_org_name || "—"], ["Buyer Contact", po.buyer_name],
                ["PO Date", fmtDate(po.po_date)], ["Required Delivery", fmtDate(po.required_delivery_date)],
                ["Expected Delivery", fmtDate(po.expected_delivery_date)], ["Actual Delivery", fmtDate(po.actual_delivery_date)],
                ["Dispatch Date", fmtDate(po.dispatch_date)], ["Transport Mode", po.transport_mode || "—"],
                ["Tracking / LR", po.tracking_ref || "—"], ["Manufacturing", po.manufacturing_status || "—"],
                ["Deliver To", po.delivery_location || "—"],
              ].map(([k, v]) => (
                <div key={k}><p className="text-xs text-muted-foreground">{k}</p><p className="font-medium">{v}</p></div>
              ))}
            </div>
          </div>

          {!["closed", "cancelled"].includes(po.status) && (
            <form onSubmit={submitUpdate} className="rounded-lg border bg-card">
              <h2 className="flex items-center gap-2 border-b px-5 py-3 font-heading text-sm font-semibold"><Truck className="h-4 w-4" /> Update Delivery Status</h2>
              <div className="grid grid-cols-1 gap-4 p-5 md:grid-cols-3">
                <div>
                  <Label>Status</Label>
                  <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                    <SelectTrigger data-testid="update-status" className="mt-1.5"><SelectValue placeholder="No change" /></SelectTrigger>
                    <SelectContent className="bg-white">
                      <SelectItem value="manufacturing">Manufacturing</SelectItem>
                      <SelectItem value="ready_for_dispatch">Ready for Dispatch</SelectItem>
                      <SelectItem value="dispatched">Dispatched</SelectItem>
                      <SelectItem value="in_transit">In Transit</SelectItem>
                      <SelectItem value="delivered">Delivered</SelectItem>
                      <SelectItem value="delayed">Delayed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Manufacturing Status</Label><Input data-testid="update-mfg" className="mt-1.5" placeholder="e.g. In Production - 60%" value={form.manufacturing_status} onChange={(e) => setForm({ ...form, manufacturing_status: e.target.value })} /></div>
                <div>
                  <Label>Transport Mode</Label>
                  <Select value={form.transport_mode} onValueChange={(v) => setForm({ ...form, transport_mode: v })}>
                    <SelectTrigger data-testid="update-transport" className="mt-1.5"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent className="bg-white">
                      {["road", "rail", "air", "sea", "courier", "other"].map((m) => <SelectItem key={m} value={m} className="capitalize">{m}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Dispatch Date</Label><Input data-testid="update-dispatch" type="date" className="mt-1.5" value={form.dispatch_date} onChange={(e) => setForm({ ...form, dispatch_date: e.target.value })} /></div>
                <div><Label>Expected Delivery Date</Label><Input data-testid="update-expected" type="date" className="mt-1.5" value={form.expected_delivery_date} onChange={(e) => setForm({ ...form, expected_delivery_date: e.target.value })} /></div>
                <div><Label>Tracking / LR Number</Label><Input data-testid="update-tracking" className="mt-1.5 font-mono" value={form.tracking_ref} onChange={(e) => setForm({ ...form, tracking_ref: e.target.value })} /></div>
                <div className="md:col-span-3"><Label>Comment for Buyer</Label><Textarea data-testid="update-comment" rows={2} className="mt-1.5" value={form.comment} onChange={(e) => setForm({ ...form, comment: e.target.value })} /></div>
              </div>
              <div className="border-t px-5 py-3">
                <Button type="submit" data-testid="update-submit" disabled={saving} className="bg-slate-900 hover:bg-slate-800"><Send className="mr-1.5 h-4 w-4" /> Submit Update</Button>
              </div>
            </form>
          )}

          <div className="rounded-lg border bg-card">
            <h2 className="border-b px-5 py-3 font-heading text-sm font-semibold">Documents</h2>
            <div className="flex items-center gap-2 border-b p-4">
              <Select value={docType} onValueChange={setDocType}>
                <SelectTrigger data-testid="doc-type-select" className="w-44"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  {[["invoice", "Invoice"], ["dispatch", "Dispatch document"], ["transport", "Transport document"], ["delivery", "Delivery document"], ["other", "Other"]].map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
              <label className="flex cursor-pointer items-center gap-1.5 rounded-md border px-3 py-2 text-sm font-medium transition-colors hover:bg-slate-50" data-testid="doc-upload-btn">
                {uploading ? "Uploading…" : <><UploadCloud className="h-4 w-4" /> Upload</>}
                <input type="file" className="hidden" data-testid="doc-upload-input" accept=".pdf,.png,.jpg,.jpeg,.xlsx,.xls,.csv,.docx,.txt,.zip" onChange={(e) => uploadDoc(e.target.files[0])} />
              </label>
            </div>
            <div className="divide-y" data-testid="po-documents">
              {docs.length === 0 && <p className="p-5 text-sm text-muted-foreground">No documents yet. Upload invoices, dispatch or transport documents here.</p>}
              {docs.map((d) => (
                <div key={d.id} className="flex items-center gap-3 px-5 py-3 text-sm">
                  <FileDown className="h-4 w-4 shrink-0 text-slate-400" />
                  <div className="min-w-0 flex-1">
                    <p className="font-medium">{d.filename}</p>
                    <p className="text-xs text-muted-foreground capitalize">{d.doc_type} · {d.uploaded_by} · <span className="font-mono">{fmtDateTime(d.created_at)}</span></p>
                  </div>
                  <a href={`${process.env.REACT_APP_BACKEND_URL}/api/documents/${d.id}/download`} target="_blank" rel="noreferrer"><Button size="sm" variant="outline">Download</Button></a>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-lg border bg-card">
            <h2 className="border-b px-5 py-3 font-heading text-sm font-semibold">Communication with Buyer</h2>
            <div className="max-h-72 space-y-3 overflow-y-auto p-5">
              {communications.length === 0 && <p className="text-sm text-muted-foreground">No messages yet.</p>}
              {[...communications].reverse().map((c) => (
                <div key={c.id} className={`max-w-[85%] rounded-lg border px-3.5 py-2.5 ${c.direction === "supplier_to_buyer" ? "ml-auto bg-slate-900 text-white" : "bg-white"}`}>
                  <p className={`text-[11px] ${c.direction === "supplier_to_buyer" ? "text-slate-300" : "text-muted-foreground"}`}>{c.sender_name} · {c.channel}</p>
                  <p className="mt-0.5 whitespace-pre-wrap text-sm">{c.message}</p>
                  <p className="mt-1 font-mono text-[10px] text-slate-400">{fmtDateTime(c.created_at)}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="rounded-lg border bg-card lg:sticky lg:top-6 lg:self-start">
          <h2 className="border-b px-5 py-3 font-heading text-sm font-semibold">PO Timeline</h2>
          <div className="max-h-[70vh] overflow-y-auto p-5"><POTimeline events={events} /></div>
        </div>
      </div>

      <Dialog open={modOpen} onOpenChange={setModOpen}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle className="font-heading">Request Delivery Date Change</DialogTitle></DialogHeader>
          <form onSubmit={submitMod} className="space-y-4">
            <div className="rounded-md bg-slate-50 p-3 text-sm">Current expected delivery: <span className="font-semibold">{fmtDate(po.expected_delivery_date)}</span></div>
            <div><Label>New Requested Date *</Label><Input data-testid="mod-date" type="date" required className="mt-1.5" value={mod.requested_date} onChange={(e) => setMod({ ...mod, requested_date: e.target.value })} /></div>
            <div><Label>Reason *</Label><Textarea data-testid="mod-reason" required rows={3} className="mt-1.5" placeholder="e.g. Production delay due to raw material shortage" value={mod.reason} onChange={(e) => setMod({ ...mod, reason: e.target.value })} /></div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setModOpen(false)}>Cancel</Button>
              <Button type="submit" data-testid="mod-submit" className="bg-slate-900 hover:bg-slate-800">Send Request</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {modifications.length > 0 && (
        <div className="rounded-lg border bg-card">
          <h2 className="border-b px-5 py-3 font-heading text-sm font-semibold">Modification Requests</h2>
          <div className="divide-y">
            {modifications.map((m) => (
              <div key={m.id} className="flex items-center gap-4 px-5 py-3 text-sm">
                <span className={`rounded-full border px-2.5 py-0.5 text-xs font-semibold ${m.status === "pending" ? "bg-[#FEF3C7] text-[#92400E] border-[#FDE68A]" : m.status === "approved" ? "bg-[#DCFCE7] text-[#166534] border-[#BBF7D0]" : "bg-[#FEE2E2] text-[#991B1B] border-[#FECACA]"}`}>{m.status}</span>
                <span>{fmtDate(m.current_date)} → <span className="font-semibold">{fmtDate(m.requested_date)}</span></span>
                <span className="flex-1 truncate text-muted-foreground">{m.reason}</span>
                <span className="font-mono text-xs text-slate-400">{fmtDateTime(m.created_at)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
